import { create } from 'zustand';
import type { ProductGridRowDto, ProductGridSaveResultDto, ProductStatus } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { changesOf, type ProductCell, type VariantCell } from '@/lib/product-grid';

/**
 * The product grid (ADR-090) — API → Store → Render, like every list.
 *
 * Cells are edited here as typed text, keyed by the product or variant they
 * belong to, so a change survives paging and filtering until it is saved or
 * discarded. Save sends only what differs from what the server sent.
 */

interface Filters {
  search: string;
  categoryId: string;
  status: ProductStatus | '';
}

interface ProductGridState extends Filters {
  rows: ProductGridRowDto[];
  nextCursor: string | null;
  total: number | null;
  loading: boolean;
  error: string | null;
  /** What was typed, per product and per variant — only cells that were touched. */
  productEdits: Record<string, Partial<Record<ProductCell, string>>>;
  variantEdits: Record<string, Partial<Record<VariantCell, string>>>;
  /** The original row a product's or variant's edits are compared with. */
  originals: Record<string, ProductGridRowDto>;
  /** Rows the last save refused, by `p:` or `v:` id, with the reason. */
  refused: Record<string, string>;
  saving: boolean;
  saveError: string | null;
  lastSaved: ProductGridSaveResultDto['saved'] | null;

  load: (patch?: Partial<Filters>) => Promise<void>;
  loadMore: () => Promise<void>;
  editProduct: (row: ProductGridRowDto, cell: ProductCell, value: string) => void;
  editVariant: (row: ProductGridRowDto, cell: VariantCell, value: string) => void;
  discard: () => void;
  save: () => Promise<void>;
}

const PAGE_SIZE = 20;
let listRequest = 0;

export const useProductGridStore = create<ProductGridState>((set, get) => ({
  search: '',
  categoryId: '',
  status: '',
  rows: [],
  nextCursor: null,
  total: null,
  loading: false,
  error: null,
  productEdits: {},
  variantEdits: {},
  originals: {},
  refused: {},
  saving: false,
  saveError: null,
  lastSaved: null,

  async load(patch = {}) {
    const filters = { search: get().search, categoryId: get().categoryId, status: get().status, ...patch };
    const request = ++listRequest;
    set({ ...filters, loading: true, error: null });
    try {
      const page = await withSession(() =>
        adminApi.catalog.grid({
          limit: PAGE_SIZE,
          search: filters.search.trim() || undefined,
          categoryId: filters.categoryId || undefined,
          status: filters.status || undefined,
        }),
      );
      if (request !== listRequest) return;
      set({ rows: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, loading: false });
    } catch (error) {
      if (request !== listRequest) return;
      set({ loading: false, error: errorMessage(error, 'Could not load the products.') });
    }
  },

  async loadMore() {
    const { nextCursor, loading, search, categoryId, status } = get();
    if (!nextCursor || loading) return;
    const request = listRequest;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.catalog.grid({
          limit: PAGE_SIZE,
          cursor: nextCursor,
          search: search.trim() || undefined,
          categoryId: categoryId || undefined,
          status: status || undefined,
        }),
      );
      if (request !== listRequest) return;
      set((state) => ({ rows: [...state.rows, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more products.') });
    }
  },

  editProduct(row, cell, value) {
    set((state) => ({
      productEdits: { ...state.productEdits, [row.productId]: { ...state.productEdits[row.productId], [cell]: value } },
      originals: state.originals[`p:${row.productId}`] ? state.originals : { ...state.originals, [`p:${row.productId}`]: row },
    }));
  },

  editVariant(row, cell, value) {
    set((state) => ({
      variantEdits: { ...state.variantEdits, [row.variantId]: { ...state.variantEdits[row.variantId], [cell]: value } },
      originals: state.originals[`v:${row.variantId}`] ? state.originals : { ...state.originals, [`v:${row.variantId}`]: row },
    }));
  },

  discard() {
    set({ productEdits: {}, variantEdits: {}, originals: {}, refused: {}, saveError: null });
  },

  async save() {
    const body = changesOf(get());
    if (!body || (body.products?.length ?? 0) + (body.variants?.length ?? 0) === 0) return;
    set({ saving: true, saveError: null, lastSaved: null });
    try {
      const result = await withSession(() => adminApi.catalog.saveGrid(body));
      const refused = Object.fromEntries(result.errors.map((error) => [`${error.kind === 'product' ? 'p' : 'v'}:${error.id}`, error.message]));
      // What landed is no longer an edit; a refused row keeps what was typed, beside its reason.
      set((state) => ({
        saving: false,
        refused,
        lastSaved: result.saved,
        productEdits: Object.fromEntries(Object.entries(state.productEdits).filter(([id]) => refused[`p:${id}`])),
        variantEdits: Object.fromEntries(Object.entries(state.variantEdits).filter(([id]) => refused[`v:${id}`])),
        originals: Object.fromEntries(Object.entries(state.originals).filter(([key]) => refused[key])),
      }));
      await get().load();
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the changes.') });
    }
  },
}));
